import React from 'react';
import FieldSelect from '../FieldSelect';
import FieldChips from '../FieldChips';
import JoinsControl from './JoinsControl';
import ConditionsControl from './ConditionsControl';
import AggsControl from './AggsControl';
import GroupsControl from './GroupsControl';
import ChartConfigControl from './ChartConfigControl';
import SeriesLabelsControl from './SeriesLabelsControl';
import { filterByKind } from './flowFields';

// Renders the editor of one node parameter, driven by the parameter type the
// server declares in the node catalog. `context` carries what the options
// come from: entities, chart types, the fields available at this node and
// the joins of the source entity.
export default function ParamControl({ param, value, onChange, context = {} }) {
  const fields = filterByKind(context.fields, param.kind);
  const label = param.label || param.key;

  if (param.type === 'text') return <TextControl label={label} value={value} onChange={onChange} />;
  if (param.type === 'int')
    return <NumberControl label={label} param={param} value={value} onChange={onChange} />;
  if (param.type === 'enum')
    return (
      <EnumControl
        label={label}
        param={param}
        options={options(param.options)}
        value={value}
        onChange={onChange}
      />
    );
  if (param.type === 'entity')
    return (
      <EnumControl
        label={label}
        param={param}
        options={options(context.entities, param.options)}
        value={value}
        onChange={onChange}
      />
    );
  if (param.type === 'chartType')
    return (
      <EnumControl
        label={label}
        param={param}
        options={options(context.chartTypes, param.options)}
        value={value}
        onChange={onChange}
      />
    );
  if (param.type === 'field')
    return <FieldSelect label={label} options={fields} value={value} onChange={onChange} />;
  if (param.type === 'field[]')
    return <FieldListControl label={label} fields={fields} value={value} onChange={onChange} />;
  if (param.type === 'joins')
    return (
      <JoinsControl label={label} joins={context.entityJoins} value={value} onChange={onChange} />
    );
  if (param.type === 'conditions')
    return (
      <ConditionsControl
        label={label}
        fields={context.fields}
        joins={context.joins}
        value={value}
        onChange={onChange}
      />
    );
  if (param.type === 'aggs')
    return (
      <AggsControl
        label={label}
        fields={fields}
        funcs={param.options}
        value={value}
        onChange={onChange}
      />
    );
  if (param.type === 'groups')
    return <GroupsControl label={label} value={value} onChange={onChange} />;
  if (param.type === 'seriesLabels')
    return (
      <SeriesLabelsControl
        label={label}
        keys={(context.fields || []).map((f) => f.key)}
        fields={context.fields}
        value={value || {}}
        onChange={onChange}
      />
    );
  if (param.type === 'chartConfig')
    return (
      <ChartConfigControl
        chartType={context.chartType}
        config={value || {}}
        fields={fields}
        onChange={onChange}
      />
    );
  return null;
}

// Options arrive either as plain strings (enums) or as { key/value, label }
// records (entities, chart types); both are normalized to { value, label }.
function options(...lists) {
  return lists
    .filter(Boolean)
    .flat()
    .map((option) =>
      option && typeof option === 'object'
        ? {
            value: option.value != null ? option.value : option.key,
            label: option.label || option.name || String(option.value)
          }
        : { value: option, label: String(option) }
    );
}

function TextControl({ label, value, onChange }) {
  const id = idFor(label);
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="form-input"
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function NumberControl({ label, param, value, onChange }) {
  const id = idFor(label);
  const placeholder = param.default != null ? String(param.default) : '';
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="form-input"
        type="number"
        value={value == null ? '' : value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </div>
  );
}

function EnumControl({ label, param, options = [], value, onChange }) {
  const id = idFor(label);
  // A handful of choices is faster to compare as chips than to scan in a list.
  if (options.length > 0 && options.length <= 4) {
    return (
      <div className="form-field">
        <span className="form-label">{label}</span>
        <div className="flow-enum-chips" role="group" aria-label={label}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className="flow-enum-chip"
              aria-pressed={value === option.value}
              onClick={() => onChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="form-field">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="form-select"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">—</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function FieldListControl({ label, fields, value, onChange }) {
  return (
    <div className="form-field">
      <span className="form-label">{label}</span>
      <FieldChips fields={fields} value={value || []} onChange={onChange} />
    </div>
  );
}

function idFor(label) {
  return `param-${String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')}`;
}
